import {
  Modal,
  NewButton,
  NewButtonVariant,
} from "@visiblethread/react-components";
import clsx from "clsx";
import { FILE_SOURCES } from "layout/documentUploader/constants";
import { LayersIcon, LogIn } from "lucide-react";
import PropTypes from "prop-types";
import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { clearSelectedCollections } from "slices/collections/collectionsSlice";
import {
  authenticateSharepoint,
  authenticateSharepointForAddIn,
  openSharepointAuthPopup,
  redirectToSharepointAuthFromCollections,
} from "slices/sharepoint/actions";
import {
  selectIsSharepointAuthenticated,
  selectSharepointAuthUrl,
  selectSharepointEnabled,
} from "slices/sharepoint/selectors";

import { modalTypes } from "../../../constants/types";
import { hideModal } from "../../../slices/app/actions/modals";
import {
  selectCollections,
  selectHasCategoriesColumn,
  selectSelectedCollections,
} from "../../../slices/collections/selectors";
import { resetState } from "../../../slices/workspace/chooseFilesModalSlice";
import EmptyState from "../../EmptyState";
import Collections from "./Collections";
import styles from "./CollectionsModal.module.scss";

const getButtonText = (count) => {
  if (count === 0) {
    return "Continue";
  }
  if (count === 1) {
    return "Continue with 1 Collection";
  }
  return `Continue with ${count} Collections`;
};

const CollectionsModal = ({ isAddIn = false }) => {
  const dispatch = useDispatch();
  const collections = useSelector(selectCollections);
  const selectedCollections = useSelector(selectSelectedCollections);
  const isSharepointEnabled = useSelector(selectSharepointEnabled);
  const isSharepointAuthenticated = useSelector(
    selectIsSharepointAuthenticated
  );
  const authUrl = useSelector(selectSharepointAuthUrl);

  const hasFilesInSelectedCollections = selectedCollections.some(
    (c) => c.files?.length > 0
  );

  const hasCategoriesColumn = useSelector(selectHasCategoriesColumn);

  const hasSharepointFile = useMemo(
    () =>
      collections?.some((collection) =>
        collection.files?.some(
          (file) => file.source === FILE_SOURCES.SHAREPOINT
        )
      ),
    [collections]
  );

  const needsSharepointAuth =
    isSharepointEnabled && hasSharepointFile && !isSharepointAuthenticated;

  useEffect(() => {
    if (!isSharepointEnabled || !hasSharepointFile || isSharepointAuthenticated) {
      return;
    }
    if (isAddIn) {
      dispatch(authenticateSharepointForAddIn());
    } else {
      dispatch(authenticateSharepoint());
    }
  }, [isAddIn, hasSharepointFile, isSharepointEnabled, isSharepointAuthenticated]);

  const handleContinue = () => {
    if (selectedCollections.length === 0 || !hasFilesInSelectedCollections) {
      return;
    }
    dispatch(hideModal(modalTypes.COLLECTIONS_MODAL));
  };

  const handleSignIn = () => {
    if (isAddIn) {
      dispatch(openSharepointAuthPopup());
    } else {
      dispatch(redirectToSharepointAuthFromCollections());
    }
  };

  const handleClose = () => {
    dispatch(resetState());
    dispatch(clearSelectedCollections());
    dispatch(hideModal(modalTypes.COLLECTIONS_MODAL));
  };

  return (
    <Modal
      className={styles["collections_modal"]}
      onDismiss={handleClose}
      restrictWidth={false}
      backdrop
      portal
      show
    >
      <Modal.Header className={styles["collections_modal__header"]}>
        <span>Use Collections</span>
      </Modal.Header>
      <Modal.Body
        className={clsx(
          styles["collections_modal__body"],
          !isAddIn && hasCategoriesColumn && styles["collections_modal__body--wide"]
        )}
      >
        {needsSharepointAuth && authUrl && (
          <div className={styles["collections_modal__auth_banner"]}>
            <LogIn size={16} />
            <span>
              Some collections contain SharePoint files. Sign in to verify
              access.
            </span>
            <NewButton variant={NewButtonVariant.l2} onClick={handleSignIn}>
              Sign In
            </NewButton>
          </div>
        )}
        {collections.length === 0 ? (
          <EmptyState
            icon={<LayersIcon size={24} color="#533ffb" />}
            title="No Collections Yet"
            description="It looks like you haven't added any collections yet. Get started by adding your first one."
          />
        ) : (
          <>
            <span className={styles["collections_modal__select_label"]}>
              Select collections below
            </span>
            <Collections isAddIn={isAddIn} />
          </>
        )}
      </Modal.Body>
      <Modal.Footer className={styles["collections_modal__footer"]}>
        <NewButton variant={NewButtonVariant.l2} onClick={handleClose}>
          Cancel
        </NewButton>

        <NewButton
          variant={NewButtonVariant.l1}
          disabled={
            selectedCollections.length === 0 ||
            !hasFilesInSelectedCollections ||
            needsSharepointAuth
          }
          onClick={handleContinue}
        >
          {getButtonText(selectedCollections.length)}
        </NewButton>
      </Modal.Footer>
    </Modal>
  );
};

CollectionsModal.propTypes = {
  isAddIn: PropTypes.bool,
};

export default CollectionsModal;
